/**
 * The pieces both action plans are built from.
 *
 * The class plan and the student plan are the same sheet read two ways — one
 * bordered block, a name strip on top, topics ranked best to worst, a ruled
 * table under each, and the band legend at the foot. Only the columns differ.
 * So the frame, the strip, the ranking bars, the tick box and the legend live
 * here and are drawn once; a heading that grows a field on one plan grows it on
 * the other, which is the thing that otherwise drifts a week after launch.
 *
 * The mastery sheet and the term report read from here too — three identical
 * definitions of the five-band ramp is exactly the duplicate that drifts.
 */
import type { ReactNode } from 'react';
import { BinGlyph, type DsBin } from '@/components/ds';
import { BIN_LABEL, BIN_NOTE, BIN_ORDER, binRamp } from '@/lib/bins';
import type { BinColor } from '@cwy/config/contracts';

export const DS_BIN: Record<BinColor, DsBin> = {
  GREEN: 'green',
  PURPLE: 'purple',
  BLUE: 'blue',
  ORANGE: 'orange',
  PINK: 'pink',
};

export const TYPE_LABEL: Record<string, string> = {
  MCQ: 'MCQ',
  SHORT_DESC: 'Short',
  MEDIUM_DESC: 'Medium',
  LONG_DESC: 'Long',
  AUDIO: 'Oral',
};

export const MODE_LABEL: Record<string, string> = {
  EXAM: 'Exam',
  MINI_TEST: 'Mini test',
  PRACTICE: 'Practice',
};

// The one grade rule, re-exported for the kit's own importers.
export { gradeLabel } from '@cwy/config/contracts';

export function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/** One labelled fact in a name strip. */
export function HeaderField({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <div
        className="text-fs-10 font-bold uppercase tracking-[0.08em]"
        style={{ color: 'var(--ink-3)' }}
      >
        {label}
      </div>
      <div className="truncate text-sm font-bold" style={{ color: 'var(--text-strong)' }}>
        {value}
      </div>
    </div>
  );
}

/** One total in a footer row. */
export function FooterStat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: string;
}) {
  return (
    <div
      className="flex flex-col gap-0.5 px-3 py-2"
      style={{ borderRight: '1px solid var(--border-subtle)' }}
    >
      <span
        className="text-fs-10 font-bold uppercase tracking-[0.08em]"
        style={{ color: 'var(--ink-3)' }}
      >
        {label}
      </span>
      <span
        data-numeric=""
        className="text-lg font-extrabold leading-none"
        style={{ color: tone ?? 'var(--text-strong)' }}
      >
        {value}
      </span>
    </div>
  );
}

/** The five bands as one proportional strip — a whole sheet in one line. */
export function BandRamp({
  counts,
  total,
  height = 10,
}: {
  counts: Record<BinColor, number>;
  total?: number;
  height?: number;
}) {
  const sum = (total ?? BIN_ORDER.reduce((s, b) => s + counts[b], 0)) || 1;
  return (
    <div
      className="flex w-full overflow-hidden rounded-full"
      style={{ background: 'var(--track)', height }}
      role="img"
      aria-label={BIN_ORDER.map((b) => `${BIN_LABEL[b]} ${counts[b]}`).join(', ')}
    >
      {BIN_ORDER.filter((b) => counts[b] > 0).map((b) => (
        <div
          key={b}
          style={{ width: `${(counts[b] / sum) * 100}%`, background: binRamp(b).border }}
        />
      ))}
    </div>
  );
}

/** A band, named and shaped. Colour is never the only carrier — see BinGlyph. */
export function BandTag({
  bin,
  note,
  size = 13,
}: {
  bin: BinColor;
  note?: boolean;
  size?: number;
}) {
  const ramp = binRamp(bin);
  return (
    <span className="inline-flex items-baseline gap-1.5" style={{ color: ramp.text }}>
      <BinGlyph bin={DS_BIN[bin]} size={9} color="currentColor" title={null} />
      <span className="font-extrabold" style={{ fontSize: size }}>
        {BIN_LABEL[bin]}
      </span>
      {note && <span className="text-fs-10 font-semibold opacity-80">· {BIN_NOTE[bin]}</span>}
    </span>
  );
}

/**
 * The tick box the paper report ends every row with.
 *
 * It is deliberately inert. The original is a circle a teacher fills in with a
 * pen once the re-teach has actually happened, and a sheet that is going to be
 * printed and worked through on a desk needs somewhere to record that. Making
 * it a real checkbox would promise the state is stored somewhere; it is not.
 */
export function TickBox({ label }: { label: string }) {
  return (
    <span
      aria-hidden="true"
      title={label}
      className="inline-block h-4 w-4 rounded-full"
      style={{ border: '1.5px solid var(--border-strong)', background: 'var(--surface-card)' }}
    />
  );
}

/**
 * Topics ranked, as a bar each.
 *
 * The paper report opens on a column chart with the topic names rotated 45° and
 * running off the foot of the page. Horizontal bars carry the same ranking and
 * let a name like "Passage reading and comprehension" sit on one readable line —
 * which matters more here than matching the original pixel for pixel, because
 * this is the part of the sheet a teacher reads first.
 */
export function TopicRankBars({
  topics,
}: {
  topics: { topicId: string; topicName: string; averagePercent: number; bin: BinColor }[];
}) {
  return (
    <ul className="flex flex-col gap-1.5">
      {topics.map((topic) => {
        const ramp = binRamp(topic.bin);
        return (
          <li key={topic.topicId} className="flex items-center gap-2">
            <span
              className="w-40 flex-none truncate text-fs-11.5 font-semibold sm:w-56"
              style={{ color: 'var(--text-body)' }}
              title={topic.topicName}
            >
              {topic.topicName}
            </span>
            <span
              className="h-4 flex-1 overflow-hidden rounded-sm"
              style={{ background: 'var(--track)' }}
            >
              <span
                className="block h-full rounded-sm"
                style={{
                  width: `${Math.max(topic.averagePercent, 1.5)}%`,
                  background: ramp.border,
                }}
              />
            </span>
            <span
              data-numeric=""
              className="w-12 flex-none text-right text-fs-11.5 font-extrabold"
              style={{ color: ramp.text }}
            >
              {topic.averagePercent}%
            </span>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * The topics that need class time, lifted to the top.
 *
 * The ranking below reads best first, which is the order the printed report has
 * always used and the order the mastery sheet's rail uses — but it buries the
 * work at the bottom of the page. This says the same thing in three lines
 * without reversing a reading order teachers already know.
 */
export function StartHere({
  title,
  items,
}: {
  title: string;
  items: { topicName: string; averagePercent: number; bin: BinColor; action: string }[];
}) {
  if (items.length === 0) return null;
  return (
    <div
      className="px-4 py-3"
      style={{
        borderBottom: '1.5px solid var(--border-strong)',
        background: 'var(--brand-lime-soft)',
      }}
    >
      <p
        className="text-fs-10 font-bold uppercase tracking-[0.08em]"
        style={{ color: 'var(--brand-ink)' }}
      >
        {title}
      </p>
      <ul className="mt-1.5 flex flex-col gap-1.5 sm:flex-row sm:flex-wrap sm:gap-x-5">
        {items.map((item) => (
          <li key={item.topicName} className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <BandTag bin={item.bin} size={12} />
            <span className="text-fs-13 font-bold" style={{ color: 'var(--text-strong)' }}>
              {item.topicName}
            </span>
            <span data-numeric="" className="text-xs font-bold" style={{ color: 'var(--ink-2)' }}>
              {item.averagePercent}%
            </span>
            <span className="text-xs" style={{ color: 'var(--ink-2)' }}>
              — {item.action}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * The band legend, which is the only reason the colours are on the page.
 *
 * The printed report closes on three lines; CWY has five bands, so it closes on
 * five. Every one of them is an instruction, not a definition — a teacher
 * reading the foot of the sheet should be able to plan Monday from it.
 */
export function BandLegend({
  actions,
  order = BIN_ORDER,
}: {
  actions: Record<BinColor, string>;
  /** Reading order, top to bottom. Defaults to Green→Pink; a teacher surface
   *  that leads with the weakest band passes it reversed. */
  order?: BinColor[];
}) {
  return (
    <div
      className="flex flex-wrap gap-x-5 gap-y-1.5 px-4 py-3"
      style={{ borderTop: '1.5px solid var(--border-strong)', background: 'var(--paper-sunk)' }}
    >
      {order.map((bin) => (
        <span key={bin} className="flex items-baseline gap-1.5 text-fs-11">
          <BandTag bin={bin} size={11} />
          <span style={{ color: 'var(--ink-2)' }}>{actions[bin]}</span>
        </span>
      ))}
    </div>
  );
}

/** The bordered block every report sheet is drawn inside. */
export function SheetFrame({ id, children }: { id: string; children: ReactNode }) {
  return (
    <section
      id={id}
      className="overflow-hidden"
      style={{
        background: 'var(--surface-card)',
        border: '1.5px solid var(--border-strong)',
        borderRadius: 'var(--radius-lg)',
      }}
    >
      {children}
    </section>
  );
}

/** The heading that opens each topic block: rank, name, band, average. */
export function TopicHeading({
  index,
  topicName,
  averagePercent,
  bin,
  right,
}: {
  index: number;
  topicName: string;
  averagePercent: number;
  bin: BinColor;
  right?: ReactNode;
}) {
  const ramp = binRamp(bin);
  return (
    <div
      className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2"
      style={{ background: ramp.fill, color: ramp.deep }}
    >
      <span className="flex items-baseline gap-2">
        <span data-numeric="" className="text-fs-13 font-extrabold">
          {index}.
        </span>
        <span className="text-fs-13 font-extrabold">{topicName}</span>
      </span>
      <span className="flex items-center gap-1.5 text-fs-11 font-bold">
        <BinGlyph bin={DS_BIN[bin]} size={9} color="currentColor" title={null} />
        {BIN_LABEL[bin]}
        <span className="font-semibold opacity-80">· {BIN_NOTE[bin]}</span>
      </span>
      <span className="ml-auto flex items-center gap-3">
        {right}
        <span data-numeric="" className="text-fs-15 font-extrabold">
          {averagePercent}%
        </span>
      </span>
    </div>
  );
}
